import React from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { useColorMode } from '../theme'

const navItems = [
  { label: 'Startseite', href: '/' },
  { label: 'Leistungen', href: '/leistungen' },
  { label: 'Referenzen', href: '/referenzen' },
  { label: 'Kontakt', href: '/kontakt' },
]

const services = [
  {
    title: 'Prozessoptimierung',
    description:
      'Workflows analysieren, Verschwendung eliminieren, Automatisierung einführen.',
  },
  {
    title: 'Softwareentwicklung',
    description:
      'Maßgeschneiderte Web-Apps, Integrationen und Schnittstellen – passgenau für Ihr Business.',
  },
  {
    title: 'Webseiten & SEO',
    description:
      'Schnelle, konvertierende Websites mit moderner Suchmaschinen-Optimierung.',
  },
]

const HomePage: React.FC = () => {
  const theme = useTheme()
  const { mode, toggleColorMode } = useColorMode()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const isDark = mode === 'dark'

  const closeDrawer = () => setDrawerOpen(false)

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Webmix-IT
          </Typography>

          {!isMobile ? (
            <Stack direction="row" spacing={1.5}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component="a"
                  href={item.href}
                  sx={{
                    color: theme.palette.text.primary,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { color: theme.palette.primary.main },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          ) : (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              aria-label="Navigation öffnen"
            >
              <MenuIcon />
            </IconButton>
          )}

          <IconButton
            onClick={toggleColorMode}
            aria-label="Theme umschalten"
            sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: 260, pt: 6 } }}
      >
        <List sx={{ px: 2 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.label}
              component="a"
              href={item.href}
              onClick={closeDrawer}
              sx={{ borderRadius: 2 }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Container sx={{ py: { xs: 8, md: 10 }, textAlign: 'center', flex: '0 0 auto' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: theme.palette.primary.main,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Ihre digitale Transformation
        </Typography>
        <Typography
          variant="h6"
          sx={{ maxWidth: 720, mx: 'auto', mb: 4, color: theme.palette.text.secondary }}
        >
          Von der Prozessoptimierung bis zur Softwareentwicklung – wir bieten umfassende
          IT-Lösungen für Ihr Unternehmen.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="contained"
            size="large"
            component="a"
            href="/kontakt"
            sx={{
              px: 4,
              py: 1.5,
            }}
          >
            Jetzt beraten lassen
          </Button>
          <Button
            variant="outlined"
            size="large"
            component="a"
            href="/leistungen"
            sx={{
              px: 4,
              py: 1.5,
            }}
          >
            Alle Leistungen ansehen
          </Button>
        </Stack>
      </Container>

      <Box sx={{ bgcolor: theme.palette.background.paper, py: { xs: 8, md: 10 }, flex: '0 0 auto' }}>
        <Container>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: theme.palette.primary.main,
            }}
          >
            Unsere Leistungen
          </Typography>

          <Grid container spacing={4}>
            {services.map((service) => (
              <Grid item xs={12} md={4} key={service.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: isDark
                      ? '0 12px 28px rgba(15, 23, 42, 0.5)'
                      : '0 12px 28px rgba(15, 23, 42, 0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: isDark
                        ? '0 18px 40px rgba(15, 23, 42, 0.55)'
                        : '0 18px 40px rgba(15, 23, 42, 0.2)',
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {service.description}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      <Button variant="contained" size="small" component="a" href="/kontakt">
                        Anfragen
                      </Button>
                      <Button variant="outlined" size="small" component="a" href="/leistungen">
                        Mehr erfahren
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 8, md: 10 }, textAlign: 'center', flex: '0 0 auto' }}>
        <Typography variant="h4" sx={{ mb: 2, color: theme.palette.primary.main }}>
          Bereit, loszulegen?
        </Typography>
        <Typography sx={{ mb: 3 }}>
          Schreiben Sie uns:{' '}
          <Box
            component="a"
            href="mailto:hallo@webmix-it.de"
            sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}
          >
            hallo@webmix-it.de
          </Box>
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="contained"
            size="large"
            component="a"
            href="/kontakt"
            sx={{ px: 4, py: 1.5 }}
          >
            Kostenloses Erstgespräch
          </Button>
          <Button
            variant="outlined"
            size="large"
            component="a"
            href="/leistungen"
            sx={{ px: 4, py: 1.5 }}
          >
            Leistungen ansehen
          </Button>
        </Stack>
      </Container>

      <Box
        component="footer"
        sx={{
          bgcolor: theme.palette.background.paper,
          py: 4,
          textAlign: 'center',
          mt: 'auto',
          borderTop: `1px solid ${theme.palette.divider}`,
          fontSize: '0.95rem',
        }}
      >
        © {new Date().getFullYear()} webmix-it.com ·{' '}
        <Box component="a" href="/impressum" sx={{ color: theme.palette.primary.main }}>
          Impressum
        </Box>{' '}
        ·{' '}
        <Box component="a" href="/datenschutz" sx={{ color: theme.palette.primary.main }}>
          Datenschutz
        </Box>
      </Box>
    </Box>
  )
}

export default HomePage
